// import { isAfter } from 'date-fns';
import { Resource, epochISOString, isEmpty } from 'idea-toolbox';

/**
 * YYYY-MM-DDTHH:MM, without timezone.
 */
type datetime = string;

export class Meal extends Resource {
  /**
   * The ID of the Meal.
   */
  mealId: string;
  /**
   * The name of the Meal.
   */
  name: string;
  /**
   * The time from which the ticket is valid.
   */
  validFrom: epochISOString;
  /**
   * The time until which the ticket is valid.
   */
  validTo: epochISOString;
  /**
   * Estabilish whether the ticket needs to be scanned by OC or is merely informative
   */
  needsScan: boolean;
  /**
   * The description of the dishes in this meal for each MealType available.
   */
  dishDescription: { [mealType: string]: string };

  load(x: any): void {
    super.load(x);
    this.mealId = this.clean(x.mealId, String);
    this.name = this.clean(x.name, String);
    this.validFrom = this.clean(x.validFrom, t => this.calcDatetimeWithoutTimezone(t));
    this.validTo = this.clean(x.validTo, t => this.calcDatetimeWithoutTimezone(t));
    this.needsScan = this.clean(x.needsScan, Boolean);
    this.dishDescription = {};
    if (x.dishDescription) this.dishDescription = x.dishDescription;
    for (const type of Object.values(MealTypes))
      if (this.dishDescription[type] === undefined) this.dishDescription[type] = '';
  }

  safeLoad(newData: any, safeData: any): void {
    super.safeLoad(newData, safeData);
    this.mealId = safeData.mealId;
  }

  validate(): string[] {
    const e = super.validate();
    if (isEmpty(this.name)) e.push('name');
    if (!this.validFrom) e.push('validFrom');
    if (!this.validTo) e.push('validTo');
    if (this.validFrom > this.validTo) e.push('validFrom', 'validTo');
    for (const type of Object.values(MealTypes)) if (!this.dishDescription[type]) e.push(`dishDescription.${type}`);
    return e;
  }

  // @todo move this to utils file so we don't have repeated code
  calcDatetimeWithoutTimezone(dateToFormat: Date | string | number, bufferInMinutes = 0): datetime {
    try {
      const date = dateToFormat instanceof Date ? new Date(dateToFormat.getTime()) : new Date(dateToFormat);
      return new Date(
        date.getTime() -
          this.convertMinutesToMilliseconds(date.getTimezoneOffset()) +
          this.convertMinutesToMilliseconds(bufferInMinutes)
      )
        .toISOString()
        .slice(0, 16);
    } catch (_) {
      // force null on invalid dates that might come from import
      return null;
    }
  }

  convertMinutesToMilliseconds(minutes: number): number {
    return minutes * 60 * 1000;
  }

  isMealValid(date: Date): boolean {
    const to = this.calcDatetimeWithoutTimezone(this.validTo);
    const from = this.calcDatetimeWithoutTimezone(this.validFrom);
    const reference = this.calcDatetimeWithoutTimezone(date);

    return reference >= from && reference <= to;
  }
}

export enum ApprovedType {
  MANUAL = 'MANUAL',
  QR_SCAN = 'QR_SCAN'
}

export enum MealTypes {
  MEAL_1 = 'MENU_1',
  MEAL_2 = 'MENU_2',
  MEAL_3 = 'MENU_3',
  SPECIAL = 'SPECIAL_MENU'
}

export interface MealCategoryAssignment {
  menu: 'MENU_1' | 'MENU_2' | 'MENU_3' | 'SPECIAL_MENU';
  color: string;
}

export const MEAL_CATEGORY_ASSIGNMENTS: Record<MealTypes, MealCategoryAssignment> = {
  [MealTypes.MEAL_1]: { menu: 'MENU_1', color: 'ESNdarkBlue' },
  [MealTypes.MEAL_2]: { menu: 'MENU_2', color: 'ESNpink' },
  [MealTypes.MEAL_3]: { menu: 'MENU_3', color: 'ESNgreen' },
  [MealTypes.SPECIAL]: { menu: 'SPECIAL_MENU', color: 'ESNcyan' }
};
